import React, { useEffect, useState, useRef, useCallback, useId } from "react";
import styles from "./EstateManage.module.scss";
import { VerticalFlex } from "../../components/flex/VerticalFlex";
import { priceFormat } from "../../utils/format";
import { dealNameColor, realPriceColor } from "../../utils/color";
import Pagination from "../../components/table/Pagination";
import { RowFlex } from "../../components/flex/RowFlex";
import icons from "../../assets/images/icons";
import { HorizontalFlex } from "../../components/flex/HorizontalFlex";
import { DefaultInputText } from "../../components/input/InputText";
import { CONFIRM_BUTTON_THEME, DefaultConfirmButton } from "../../components/buttons/ConfirmButton";
import legacy from "../../utils/legacy";
import { createPortal } from "react-dom";
import OwnerVerificationBadge from "./OwnerVerificationBadge";
import { OwnerVerifiedGradientBadge } from "../../components/badge/OwnerVerifiedBadge";

import { getData, postJsonData } from "../../services/baseApi";
import { toast } from "../../components/material/ToastPopup";


function isOwnerVerified(data) {
  const checkCompleted = (status, expiresAt) => {
    if (status !== 'COMPLETED') return false;
    if (!expiresAt) return true;
    const now = new Date(); now.setHours(0,0,0,0);
    const expires = new Date(expiresAt); expires.setHours(0,0,0,0);
    return expires >= now;
  };
  return checkCompleted(data.ov_active_status, data.ov_active_expires_at)
    || checkCompleted(data.ov_status, data.ov_expires_at);
}

export const DataTableViewType = {
  TABLE: "table",
  LIST: "list",
};
const EstateManageDataTable = ({
  data = {},
  checkedList = [],
  viewType = DataTableViewType.TABLE,
  isMobile = false,
  hasMore = false,
  payload = {},
  onCheckItem = () => {},
  onSetCheckedList = () => {},
  onSetPayload = () => {},
  onUpdateAddressOpen = () => {},
  onUpdateStateType = () => {},
  onUpdateMemo = () => {},
  onDeleteSale = () => {},
  onDetailSaleView = () => {},
  onOvBadgeClick = () => {},
  onOvHelpClick = () => {},
}) => {
  const tableContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollInterval = useRef(null);
  const isManualScroll = useRef(false);

  const SCROLL_STEP = 150;
  const columns = [
    { label: "", key: "checkbox", fixed: true, onClick: () => {} },
    { label: "소유자 인증", key: "ov_status" },
    { label: "상태 / 유형", key: "type" },
    { label: "매물사진", key: "photo" },
    { label: "금액", key: "price" },
    { label: "주소", key: "address" },
    { label: "날짜", key: "date" },
    { label: "노출 설정", key: "control" },
    { label: "부가정보 관리", key: "addition" },
    { label: "관리", key: "manage", fixed: true },
  ];

  const handleChecked = (val) => {
    onCheckItem(val);
  };

  const handlePayload = (key, value) => {
    onSetPayload((prev) => {
      if (key === "offset") {
        const newOffset = prev.limit ? prev.limit[0] * value : value;
        if (prev.offset !== newOffset) {
          return {
            ...prev,
            offset: newOffset,
          };
        }
        return prev;
      }

      if (prev[key] !== value) {
        return {
          ...prev,
          [key]: value,
        };
      }

      return prev;
    });
  };

  const getIsAllChecked = () => {
    const allSuidList = data?.data?.map((item) => item.suid) || [];
    const isAllChecked =
      allSuidList.every((suid) => checkedList.includes(suid)) && checkedList.length > 0;
    return isAllChecked;
  };

  const handleCheckAll = () => {
    const allSuidList = data?.data?.map((item) => item.suid) || [];
    const isAllChecked = allSuidList.every((suid) => checkedList.includes(suid));
    if (isAllChecked) {
      onSetCheckedList((prev) => prev.filter((suid) => !allSuidList.includes(suid)));
    } else {
      onSetCheckedList((prev) => [...new Set([...prev, ...allSuidList])]);
    }
  };

  useEffect(() => {
    const updateScrollButtons = () => {
      if (!tableContainerRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };

    const attachScrollListener = () => {
      if (tableContainerRef.current) {
        updateScrollButtons();
        tableContainerRef.current.addEventListener("scroll", updateScrollButtons);
      }
    };

    attachScrollListener();
    window.addEventListener("resize", updateScrollButtons);

    const observer = new MutationObserver(() => {
      attachScrollListener();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (tableContainerRef.current) {
        tableContainerRef.current.removeEventListener("scroll", updateScrollButtons);
      }
      window.removeEventListener("resize", updateScrollButtons);
      observer.disconnect();
    };
  }, [tableContainerRef.current]);

  const scrollToEnd = (direction) => {
    if (tableContainerRef.current) {
      if (direction === "left") {
        tableContainerRef.current.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        tableContainerRef.current.scrollTo({
          left: tableContainerRef.current.scrollWidth,
          behavior: "smooth",
        });
      }
    }
  };

  const loadMoreRef = useRef(null);
  useEffect(() => {
    if (!isMobile) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          handlePayload("offset", data.offset + 1);
        }
      },
      { threshold: 1.0 }
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [data?.offset, hasMore]);

  const renderTable = () => {
    return (
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          flexGrow: "1",
          paddingLeft: "20px",
        }}
      >
        <div
          ref={tableContainerRef}
          className={`${styles.tableContainer} ${styles.tableItemStyles}`}
        >
          <table>
            <thead className={`${styles.stickyHeader}`}>
              <tr>
                {columns.map((header) => {
                  return header.key == "checkbox" ? (
                    <th
                      height="48"
                      key={header.key}
                      className={`${header.fixed ? styles.fixedCell : ""}`}
                      onClick={() => handleCheckAll()}
                    >
                      <CheckboxCell key={`${header.key}_box`} checked={getIsAllChecked()} />
                    </th>
                  ) : (
                    <th
                      height="48"
                      key={header.key}
                      className={`${header.fixed ? styles.fixedCell : ""}`}
                    >
                      {header.label}
                      {header.key === "ov_status" && (
                        <button
                          type="button"
                          className={styles.ovHelpBtn}
                          onClick={onOvHelpClick}
                          aria-label="소유자 인증 안내"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="7.5" stroke="rgba(112,115,124,0.4)" />
                            <text x="8" y="12" textAnchor="middle" fontSize="11" fontWeight="500" fill="rgba(112,115,124,0.61)">?</text>
                          </svg>
                        </button>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data?.data?.length > 0 ? (
                data?.data?.map((row, index) => (
                  <RenderRow
                    key={index}
                    data={row}
                    columns={columns}
                    checkedList={checkedList}
                    onCheckItem={handleChecked}
                    onUpdateAddressOpen={onUpdateAddressOpen}
                    onUpdateStateType={onUpdateStateType}
                    onUpdateMemo={onUpdateMemo}
                    onDetailSaleView={onDetailSaleView}
                    onDeleteSale={onDeleteSale}
                    onOvBadgeClick={onOvBadgeClick}
                  />
                ))
              ) : (
                <tr className={`${styles.notaction}`}>
                  <td
                    colSpan={columns.length}
                    className={styles.noData}
                    style={{ textAlign: "center" }}
                  >
                    <img
                      src={icons.saleNoData}
                      style={{ width: "80px", height: "80px" }}
                      alt="No Data"
                    />
                    {payload.keyword ? (
                      <div>
                        <p>
                          <span style={{ color: "#0868f4", fontWeight: "bold" }}>
                            {payload.keyword}
                          </span>
                          에 대한 검색결과가 없어요!
                        </p>
                        <p>혹시 검색어를 다시 확인해 보시겠어요?</p>
                      </div>
                    ) : (
                      <p>등록된 매물이 없습니다.</p>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {canScrollLeft && (
          <div className={`${styles.scrollBtnContainer} ${styles.left}`}>
            <button className={`${styles.scrollBtn}`} onClick={() => scrollToEnd("left")}>
              <img src={icons.squareArrow.left.black} />
            </button>
          </div>
        )}
        {canScrollRight && (
          <div className={`${styles.scrollBtnContainer} ${styles.right}`}>
            <button className={`${styles.scrollBtn}`} onClick={() => scrollToEnd("right")}>
              <img src={icons.squareArrow.right.black} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderList = () => {
    return (
      <div
        className={`${styles.listContainer} ${styles.listItemStyles} ${isMobile ? styles.bottomPadding : ""}`}
      >
        {data?.data?.length > 0 ? (
          data?.data?.map((row, index) => (
            <RenderItem
              key={index}
              data={row}
              columns={columns}
              checkedList={checkedList}
              onCheckItem={handleChecked}
              onUpdateAddressOpen={onUpdateAddressOpen}
              onUpdateStateType={onUpdateStateType}
              onUpdateMemo={onUpdateMemo}
              onDeleteSale={onDeleteSale}
              onDetailSaleView={onDetailSaleView}
              onOvBadgeClick={onOvBadgeClick}
              isMobile={isMobile}
            />
          ))
        ) : (
          <div className={`${styles.notaction} ${styles.listNoData}`}>
            <div className={styles.noData} style={{ textAlign: "center" }}>
              <img src={icons.saleNoData} style={{ width: "80px", height: "80px" }} alt="No Data" />
              {payload.keyword ? (
                <div className={`${styles.wrap}`}>
                  <div>
                    <div style={{ color: "#0868f4" }}>{payload.keyword}</div>
                    <div style={{ marginTop: "4px" }}>에 대한 검색결과가 없어요!</div>
                  </div>
                  <div className={`${styles.nText}`}>검색어를 다시 확인해 보시겠어요?</div>
                </div>
              ) : (
                <div>등록된 매물이 없습니다.</div>
              )}
            </div>
          </div>
        )}
        {isMobile && hasMore && <div ref={loadMoreRef} className={styles.loadMoreTrigger} />}
      </div>
    );
  };

  return (
    data?.data && (
      <VerticalFlex
        className={`${styles.dataContainer} ${viewType == DataTableViewType.TABLE && !isMobile ? styles.fullMaxSize : ""}`}
      >
        {viewType == DataTableViewType.TABLE && !isMobile ? renderTable() : renderList()}
        {!isMobile && (
          <div className={`${styles.paginationContainer}`}>
            <Pagination
              totalPages={data.pages}
              currentPage={data.offset + 1}
              onPageChange={(idx) => handlePayload("offset", idx - 1)}
              maxPageNumbers={viewType === DataTableViewType.TABLE ? 10 : 5}
            />
          </div>
        )}
      </VerticalFlex>
    )
  );
};

export { EstateManageDataTable };

const CheckboxCell = ({ name, value, checked }) => {
  return (
    <div className={styles.checkboxWrapper}>
      <div className={`${styles.checkbox} ${checked ? styles.checked : ""}`} />
    </div>
  );
};

const TypeCell = ({ data }) => {
  const getState = (data) => {
    if (data.state_type == 0) {
      if (data.ad == 1) {
        return (
          <div className={`${styles.typeItem} ${styles.state} ${styles.ad}`}>
            <div>&#x2022;</div>광고중
          </div>
        );
      }
      return (
        <div className={`${styles.typeItem} ${styles.state} ${styles.release}`}>
          <div>&#x2022;</div>
          <div>노출중</div>
        </div>
      );
    } else if (data.state_type == 1) {
      return (
        <div className={`${styles.typeItem} ${styles.state} ${styles.done}`}>
          <div>거래완료</div>
        </div>
      );
    } else {
      return (
        <div className={`${styles.typeItem} ${styles.state} ${styles.hide}`}>
          <div>비노출</div>
        </div>
      );
    }
  };

  return (
    <div className={styles.typeCell}>
      {getState(data)}
      <div
        className={`${styles.typeItem}`}
        style={{ ...realPriceColor(legacy.real_price_type(data.estate_type)) }}
      >
        {legacy.ifo_get_rand_type(data.e_rand_type)}
      </div>
    </div>
  );
};

const getPhotoUrl = (photoUrl) => {
  if (!photoUrl) return null;
  const photos = photoUrl.split("|");
  for (let i = 0; i < photos.length; i++) {
    if (photos[i].indexOf("http") < 0) {
      return legacy.server_by_hostname("aurora", "files/sale_photo/72x72_") + photos[i];
    }
  }
  return null;
};

const PhotoCell = ({ photoUrl }) => {
  const photo = getPhotoUrl(photoUrl);
  return photo ? (
    <div className={styles.photoCell} style={{ backgroundImage: `url(${getPhotoUrl(photoUrl)}` }} />
  ) : (
    <div
      className={`${styles.photoCell} ${styles.blank}`}
      style={{ backgroundImage: `url(${icons.blankImage.sale}` }}
    />
  );
};

const PriceCell = ({ dealType, price, deposit, monthly }) => {
  const dealName = ["매매", "전세", "월세"][dealType] || "미등록";
  const dealColor = dealNameColor(dealName);
  const formattedPrice = price
    ? priceFormat(price)
    : monthly > 0
      ? `${priceFormat(deposit)} / ${priceFormat(monthly)}`
      : "미등록";
  return (
    <div className={styles.priceCell}>
      <div className={styles.dealName} style={{ color: dealColor }}>
        {dealName}
      </div>
      <div className={styles.price}>{formattedPrice}</div>
    </div>
  );
};

const AddressCell = ({ address, buildingName, data }) => {
  const getConinfo = (data) => {
    const to_area = legacy.to_area;
    var con_info = "";
    if (data["e_rand_type"] === 16) {
      con_info +=
        to_area(data["land_area"]) +
        " ・ " +
        legacy.ifo_get_land_purpose_type(data["land_purpose"]);
    } else if (
      [0, 1, 2, 7, 8, 10, 11, 18, 20].indexOf(data["e_rand_type"]) !== -1 ||
      ([4, 6].indexOf(data["e_rand_type"]) !== -1 && data["deal_type"] !== 0)
    ) {
      var floor = "";
      if (data["floor_type"] == null || data["floor_type"] < 0) {
        floor = data["floor_cnt"] + " / " + data["floor_total"];
      } else {
        if (data["floor_type"] == 0) floor = "고";
        if (data["floor_type"] == 1) floor = "중";
        if (data["floor_type"] == 2) floor = "저";
      }

      var area_nm = [2, 7, 8, 10, 11, 18, 20].indexOf(data["e_rand_type"]) !== -1 ? "계약 " : "공급 ";
      con_info +=
        area_nm +
        to_area(data["supply_area"]) +
        " ・  전용 " +
        to_area(data["floor_area"]) +
        " ・ " +
        floor +
        "층";
    } else {
      con_info +=
        "대지 " +
        to_area(data["land_area"]) +
        " ・ 연 " +
        to_area(data["floor_area"]) +
        " ・ " +
        data["floor_cnt"] +
        "F / B" +
        data["under_floor"];
    }
    return con_info;
  };
  return (
    <div className={styles.addressCell}>
      {/* <div className={styles.buildingName}>{address} {buildingName && `(${buildingName})`}</div> */}
      <div className={styles.buildingName}>{address}</div>
      <div className={styles.details} dangerouslySetInnerHTML={{ __html: getConinfo(data) }} />
    </div>
  );
};

const DateCell = ({ creationDate }) => {
  const [year, month, day] = creationDate.split(".");
  return (
    <div className={styles.dateCell}>
      <DateDiifCell creationDate={creationDate} />
      <div className={styles.shortDate}>{`${year.slice(2)}/${month}/${day}`}</div>
    </div>
  );
};

const DateDiifCell = ({ creationDate }) => {
  return (
    <div className={styles.agoDate}>{legacy.build_due_str_for_creation(creationDate)?.[0]}</div>
  );
};

const ToggleButton = ({ title, checked, onToggle, disabled = false }) => {
  const toggleId = useId(); // 고유한 id 생성 (React 18+)

  return (
    <div
      className={`${styles.toogleSwitchContainer}`}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <input id={toggleId} type="checkbox" checked={checked} readOnly />
      <label
        htmlFor={toggleId}
        className={`${styles.toggleSwitch} ${disabled ? styles.disabled : ""}`}
        onClick={() => {
          if (!disabled) {
            onToggle(!checked);
          }
        }}
      >
        <span>{checked ? title.checked : title.default}</span>
        <span className={styles.slider} />
      </label>
    </div>
  );
};

const AdditionButton = ({ title, icon, deactive, children, onToggle }) => {
  const [openPopup, setOpenPopup] = useState(false);
  const popupRef = useRef(null);
  const handleClick = (e) => {
    e.stopPropagation();
    setOpenPopup(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setOpenPopup(false);
      }
    };

    if (openPopup) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    }

    onToggle?.(openPopup);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [openPopup]);

  const closePopup = () => {
    setOpenPopup(false);
  };

  return (
    <div className={`${styles.additionButtonWrapper}`}>
      <button
        className={`${styles.additionButton} ${!deactive ? styles.active : ""}`}
        onClick={handleClick}
      >
        <img src={icon} alt="icon" />
        <div className={`${styles.label}`}>{title}</div>
      </button>

      {openPopup && (
        <div
          ref={popupRef}
          className={`${styles.additionButtonPopup}`}
          onClick={(e) => e.stopPropagation()}
        >
          <HorizontalFlex
            className={`${styles.header}`}
            leftChildren={<div style={{ marginTop: "8px" }}>{title}</div>}
            rightChildren={
              <div
                className={styles.cursorPointer}
                style={{ padding: "12px" }}
                onClick={closePopup}
              >
                <img src={icons.close.black} style={{ width: "24px", height: "24px" }} />
              </div>
            }
          />
          {children}
        </div>
      )}
    </div>
  );
};

const MemoEditor = ({ onClick, data }) => {
  const confirmStyle = {
    width: "100%",
    fontSize: "16px",
    fontWeight: "normal",
    padding: "14px 0px",
  };

  const getInitial = () => ({
    sell_name: data.sell_name || "",
    sell_phone: data.sell_phone || "",
    buy_name: data.buy_name || "",
    buy_phone: data.buy_phone || "",
    memo: data.memo || "",
  });

  const initialMemo = getInitial();

  const [memo, setMemo] = useState(initialMemo);
  const [isChanged, setIsChanged] = useState(false);

  const handleChange = (field, value) => {
    setMemo((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeleteMemo = () => {
    setMemo({
      sell_name: "",
      sell_phone: "",
      buy_name: "",
      buy_phone: "",
      memo: "",
    });
  };

  const handleSave = () => {
    onClick?.(data.suid, memo);
  };

  useEffect(() => {
    setIsChanged(JSON.stringify(memo) !== JSON.stringify(initialMemo));
  }, [memo, initialMemo]);

  return (
    <div className={`${styles.body}`}>
      <VerticalFlex gap="12px">
        <RowFlex gap="12px">
          <DefaultInputText
            label={"매도/임대인"}
            placeholder={"홍길동"}
            value={memo.sell_name}
            onChange={(val) => handleChange("sell_name", val)}
          />
          <DefaultInputText
            label={"전화번호"}
            placeholder={"010-0000-0000"}
            value={memo.sell_phone}
            onChange={(val) => handleChange("sell_phone", val)}
          />
        </RowFlex>
        <RowFlex gap="12px">
          <DefaultInputText
            label={"매수/임차인"}
            placeholder={"홍길동"}
            value={memo.buy_name}
            onChange={(val) => handleChange("buy_name", val)}
          />
          <DefaultInputText
            label={"전화번호"}
            placeholder={"010-0000-0000"}
            value={memo.buy_phone}
            onChange={(val) => handleChange("buy_phone", val)}
          />
        </RowFlex>
        <DefaultInputText
          label={"메모"}
          placeholder={"메모"}
          multiline={true}
          value={memo.memo}
          onChange={(val) => handleChange("memo", val)}
        />
        <RowFlex gap="8px" style={{ marginTop: "4px" }}>
          <DefaultConfirmButton
            theme={CONFIRM_BUTTON_THEME.GREY}
            label={"메모 삭제"}
            style={confirmStyle}
            onClick={handleDeleteMemo}
          />
          <DefaultConfirmButton
            label={"저장"}
            style={confirmStyle}
            disabled={!isChanged}
            onClick={handleSave}
          />
        </RowFlex>
      </VerticalFlex>
    </div>
  );
};
const AdditionButtonsMemo = ({ title, icon, onClick, deactive, data }) => {
  return (
    <AdditionButton title={title} icon={icon} deactive={deactive}>
      <MemoEditor data={data} onClick={onClick} />
    </AdditionButton>
  );
};

const AdditionButtonsReport = ({ title, icon, onClick, deactive, data }) => {
  return (
    <div className={`${styles.additionButtonWrapper}`}>
      <button
        className={`${styles.additionButton} ${!deactive ? styles.active : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
      >
        <img src={icon} alt="icon" />
        <div className={`${styles.label}`}>{title}</div>
      </button>
    </div>
  );
};

const AI_POST_POLL_INTERVAL_MS = 5000;
const AI_POST_POLL_MAX_TRIES = 18; // 5s × 18 = 1m30s
const AI_POST_REMAIN_REFRESH_EVENT = "ai-post-remain-refresh";

const escapeAiPostHtml = (text) => {
  const div = document.createElement("div");
  div.textContent = text || "";
  return div.innerHTML.replace(/\n/g, "<br>");
};

const escapeAiPostAttr = (url) =>
  String(url || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const AI_POST_PARAGRAPH_STYLE = "font-size:16px;line-height:2;text-align:center;";
const AI_POST_BODY_HEADING_STYLE = "font-size:22px;font-weight:700;line-height:1.6;text-align:center;";
const AI_POST_IMAGE_WRAP_STYLE = "text-align:center;";

const AI_POST_BLOCK_SPACER = "<div><br></div><div><br></div>";

const renderAiPostBody = (body) => {
  const firstBreak = body.indexOf("\n");
  if (firstBreak < 0) {
    return `<div style="${AI_POST_BODY_HEADING_STYLE}">${escapeAiPostHtml(body)}</div>`;
  }
  const heading = body.slice(0, firstBreak);
  const rest = body.slice(firstBreak).replace(/^\n+/, "");
  let out = `<div style="${AI_POST_BODY_HEADING_STYLE}">${escapeAiPostHtml(heading)}</div><div><br></div>`;
  if (rest) {
    out += `<div style="${AI_POST_PARAGRAPH_STYLE}">${escapeAiPostHtml(rest)}</div>`;
  }
  return out;
};

const renderSaleLinkCard = (saleLink) => {
  if (!saleLink || !saleLink.url) return "";
  const href = escapeAiPostAttr(saleLink.url);
  const photo = saleLink.photo
    ? `<div class="ai-post-sale-link-photo"><img src="${escapeAiPostAttr(saleLink.photo)}"></div>`
    : "";
  const tag = saleLink.type
    ? `<div class="ai-post-sale-link-tag">#${escapeAiPostHtml(saleLink.type)}</div>`
    : "";
  const dealType = saleLink.deal_type
    ? `<span class="deal-type">${escapeAiPostHtml(saleLink.deal_type)}</span> `
    : "";
  const price = saleLink.price ? escapeAiPostHtml(saleLink.price) : "";
  return (
    `<div style="text-align:center;">` +
    `<a href="${href}" class="ai-post-sale-link ai-post-sale-link-click" data-suid="${escapeAiPostAttr(saleLink.suid || "")}" onclick="event.preventDefault();">` +
    `<div class="ai-post-sale-link-header"><span>매물 바로가기</span></div>` +
    `<div class="ai-post-sale-link-body">` +
    photo +
    `<div class="ai-post-sale-link-info">` +
    tag +
    `<div class="ai-post-sale-link-addr">${escapeAiPostHtml(saleLink.address || "")}</div>` +
    `<div class="ai-post-sale-link-price">${dealType}${price}</div>` +
    `</div>` +
    `<div class="ai-post-sale-link-chevron">›</div>` +
    `</div></a></div>`
  );
};

const renderAgentCard = (agent) => {
  if (!agent || !agent.ceo_name) return "";
  const photoUrl = agent.ceo_photo
    ? legacy.server_by_hostname("aurora", "files/user_photo/100x100_" + agent.ceo_photo)
    : "";
  const photo = photoUrl
    ? `<div class="ai-post-agent-photo"><img src="${escapeAiPostAttr(photoUrl)}"></div>`
    : "";
  const isAgent = ["1", "2", "3"].includes(agent.agtype);
  const phoneFormatted = (agent.phone || "").replace(/-/g, " ");
  let rows = "";
  if (isAgent) {
    if (agent.reg_number)
      rows += `<tr><td class="label">등록번호</td><td class="value">${escapeAiPostHtml(agent.reg_number)}</td></tr>`;
    if (agent.addr_full)
      rows += `<tr><td class="label">소재지</td><td class="value">${escapeAiPostHtml(agent.addr_full)}</td></tr>`;
    rows += `<tr><td class="label">대표</td><td class="value">${escapeAiPostHtml(agent.ceo_name)}</td></tr>`;
  }
  if (phoneFormatted)
    rows += `<tr><td class="label">대표연락처</td><td class="phone">${escapeAiPostHtml(phoneFormatted)}</td></tr>`;
  return (
    `<div style="text-align:center;">` +
    `<div class="ai-post-agent-card">` +
    `<div class="ai-post-agent-header">` +
    photo +
    `<div>` +
    `<div class="ai-post-agent-name">${escapeAiPostHtml(agent.ceo_name)}</div>` +
    `<div class="ai-post-agent-org">${escapeAiPostHtml(agent.org || "")}</div>` +
    `</div></div>` +
    (rows
      ? `<hr class="ai-post-agent-divider"><table class="ai-post-agent-table">${rows}</table>`
      : "") +
    `</div></div>`
  );
};

const buildAiPostHtml = (intro, body, cardUrl1, cardUrl2, summary, saleLink, agentCard) => {
  let html = "";
  if (cardUrl1) {
    html += `<div style="${AI_POST_IMAGE_WRAP_STYLE}"><img class="wizwig-thumb-image" src="${escapeAiPostAttr(cardUrl1)}" style="max-width:540px;width:100%;"></div>${AI_POST_BLOCK_SPACER}`;
  }
  if (intro) {
    html += `<div style="${AI_POST_PARAGRAPH_STYLE}">${escapeAiPostHtml(intro)}</div>${AI_POST_BLOCK_SPACER}`;
  }
  if (cardUrl2) {
    const spacer = summary ? "<div><br></div>" : AI_POST_BLOCK_SPACER;
    html += `<div style="${AI_POST_IMAGE_WRAP_STYLE}"><img src="${escapeAiPostAttr(cardUrl2)}" style="max-width:540px;width:100%;"></div>${spacer}`;
  }
  if (summary) {
    const trimmed = summary.replace(/\n{2,}/g, "\n");
    html += `<div style="${AI_POST_PARAGRAPH_STYLE}">${escapeAiPostHtml(trimmed)}</div>${AI_POST_BLOCK_SPACER}`;
  }
  if (body) {
    html += renderAiPostBody(body);
  }
  html += AI_POST_BLOCK_SPACER;
  html += renderSaleLinkCard(saleLink);
  html += AI_POST_BLOCK_SPACER;
  html += renderAgentCard(agentCard);
  return html;
};

const openWizwigWithAiPostResult = (data, res) => {
  const content = buildAiPostHtml(
    res.intro || "",
    res.body || "",
    res.card_01_url || "",
    res.card_02_url || "",
    res.summary || "",
    res.sale_link || null,
    res.agent_card || null
  );
  const context = {
    title: data.title || [data.address, data.building_name].filter(Boolean).join(" "),
    content,
    thumbnail: res.card_01_url || null,
  };
  const tryOpen = () => {
    if (typeof window.blogOpenEditor === "function") {
      window.blogOpenEditor(context);
      return true;
    }
    return false;
  };
  if (tryOpen()) return;
  if (window.requireHTML?.get && window.advanceRequireJS) {
    window.requireHTML.get("agent/disco_agent_blog.html", () => {
      window.advanceRequireJS("agent/js/disco_agent_blog.js", () => {
        tryOpen();
      });
    });
  } else {
    console.warn("blogOpenEditor 로더를 찾지 못함");
  }
};

const AiPostLoadingOverlay = ({ message }) => {
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <style>{"@keyframes aipostSpin{to{transform:rotate(360deg)}}"}</style>
      <div
        style={{
          background: "#fff",
          padding: "28px 32px",
          borderRadius: 10,
          minWidth: 280,
          maxWidth: "90vw",
          textAlign: "center",
        }}
      >
        <div
          style={{
            margin: "0 auto 14px",
            width: 36,
            height: 36,
            border: "4px solid #e6eef5",
            borderTopColor: "#0868f4",
            borderRadius: "50%",
            animation: "aipostSpin 0.9s linear infinite",
          }}
        />
        <div style={{ fontSize: 15, color: "#1c1c1e", lineHeight: 1.5 }}>
          {message || "AI가 포스트를 작성하고 있어요!"}
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: "#8e8e93" }}>
          최대 1분 30초 정도 걸릴 수 있어요.
        </div>
      </div>
    </div>,
    document.body
  );
};

const runAiPostFlow = async (data, { setLoadingMessage, onFinish, isCancelled }) => {
  const cancelled = () => (typeof isCancelled === "function" ? isCancelled() : false);
  try {
    const start = await postJsonData("/sale/ai_create_post/", {
      sale_uuid: data.suid,
    });
    if (cancelled()) return;
    if (!start || !start.job_id) {
      toast.error("AI 작업 접수에 실패했어요.");
      onFinish();
      return;
    }
    const jobId = start.job_id;

    for (let tries = 0; tries < AI_POST_POLL_MAX_TRIES; tries++) {
      await new Promise((resolve) => setTimeout(resolve, AI_POST_POLL_INTERVAL_MS));
      if (cancelled()) return;
      let res;
      try {
        res = await getData(`/sale/ai_post_status/${encodeURIComponent(jobId)}/`);
      } catch (_) {
        continue;
      }
      if (cancelled()) return;
      if (!res || !res.status) {
        toast.error("AI 작업 상태 조회에 실패했어요.");
        onFinish();
        return;
      }
      if (res.status === "done") {
        onFinish();
        window.dispatchEvent(new Event(AI_POST_REMAIN_REFRESH_EVENT));
        openWizwigWithAiPostResult(data, res);
        return;
      }
      if (res.status === "failed") {
        toast.error("AI 포스트 작성에 실패했어요. 잠시 후 다시 시도해주세요.");
        onFinish();
        return;
      }
      if (res.text_status === "done" && res.image_status !== "done") {
        setLoadingMessage("카드 이미지를 만들고 있어요...");
      } else if (res.image_status === "done" && res.text_status !== "done") {
        setLoadingMessage("문구를 다듬고 있어요...");
      } else {
        setLoadingMessage("AI가 포스트를 작성하고 있어요!");
      }
    }
    toast.error("AI 포스트 작성이 1분 30초 안에 끝나지 않았어요. 잠시 후 다시 시도해주세요.");
    onFinish();
  } catch (err) {
    if (cancelled()) return;
    toast.error("AI 포스트 작성에 실패했어요. 잠시 후 다시 시도해주세요.");
    onFinish();
  }
};

let _aiPostRemainCache = null;
let _aiPostRemainInflight = null;
const _aiPostRemainSubscribers = new Set();

const _fetchAiPostRemain = () => {
  if (_aiPostRemainInflight) return _aiPostRemainInflight;
  _aiPostRemainInflight = getData("/sale/ai_remain_count/")
    .then((res) => {
      const n =
        res && typeof res.post_remain_count !== "undefined"
          ? res.post_remain_count
          : res && res.remain_count;
      if (typeof n === "number") {
        _aiPostRemainCache = n;
        _aiPostRemainSubscribers.forEach((fn) => fn(n));
      }
    })
    .catch(() => {})
    .finally(() => {
      _aiPostRemainInflight = null;
    });
  return _aiPostRemainInflight;
};

const useAiPostRemain = () => {
  const [remain, setRemain] = useState(_aiPostRemainCache);

  useEffect(() => {
    _aiPostRemainSubscribers.add(setRemain);
    if (_aiPostRemainCache === null) _fetchAiPostRemain();
    const handler = () => _fetchAiPostRemain();
    window.addEventListener(AI_POST_REMAIN_REFRESH_EVENT, handler);
    return () => {
      _aiPostRemainSubscribers.delete(setRemain);
      window.removeEventListener(AI_POST_REMAIN_REFRESH_EVENT, handler);
    };
  }, []);

  return remain;
};

const useAiPostStarter = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const startAiPost = useCallback(
    (data) => {
      if (loading) return;
      if (!data?.photo) {
        toast.error("매물에 등록된 사진이 없습니다. 사진을 먼저 등록해주세요.");
        return;
      }
      setLoadingMessage("AI가 포스트를 작성하고 있어요!");
      setLoading(true);
      runAiPostFlow(data, {
        setLoadingMessage,
        onFinish: () => {
          if (!cancelledRef.current) setLoading(false);
        },
        isCancelled: () => cancelledRef.current,
      });
    },
    [loading]
  );
  const loadingNode = loading ? <AiPostLoadingOverlay message={loadingMessage} /> : null;
  return { startAiPost, loadingNode, loading };
};

const AdditionButtonsAiPost = ({ data, onStart, disabled }) => {
  const remain = useAiPostRemain();
  const label =
    typeof remain === "number" ? `AI 포스트 (${remain}회 남음)` : "AI 포스트";

  return (
    <div className={`${styles.additionButtonWrapper}`}>
      <button
        className={`${styles.additionButton}`}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onStart?.(data);
        }}
      >
        <img src={icons.aiPost.grey} alt="icon" />
        <div className={`${styles.label}`}>{label}</div>
      </button>
    </div>
  );
};

const ManageCell = ({ items, style = {} }) => {
  const [openPopup, setOpenPopup] = useState(false);
  const popupRef = useRef(null);
  const buttonRef = useRef(null);
  const [popupStyle, setPopupStyle] = useState({});

  const updatePopupPosition = () => {
    if (buttonRef.current && popupRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popupWidth = popupRef.current.offsetWidth || 0;
      const popupHeight = popupRef.current.offsetHeight || 0;
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const shouldPlaceOnTop = rect.bottom + popupHeight + 10 > windowHeight;

      setPopupStyle({
        position: "fixed",
        top: shouldPlaceOnTop ? `${rect.top - popupHeight - 10}px` : `${rect.bottom + 10}px`,
        left:
          rect.right + popupWidth > windowWidth
            ? `${rect.right - popupWidth}px`
            : `${rect.right - popupWidth}px`,
        maxHeight: shouldPlaceOnTop ? `${rect.top - 10}px` : `${windowHeight - rect.bottom - 10}px`,
        overflowY: "auto",
      });
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    setOpenPopup((prev) => {
      const newState = !prev;
      if (newState) {
        setTimeout(updatePopupPosition, 0);
      }
      return newState;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setOpenPopup(false);
      }
    };

    const handleScroll = () => {
      setOpenPopup(false);
    };

    if (openPopup) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openPopup]);

  const handleItemClick = (e, item) => {
    e.stopPropagation();
    setOpenPopup(false);
    item.onClick?.(e);
  };

  const popupContent = (
    <div ref={popupRef} className={styles.mcDropDownContainer} style={popupStyle}>
      {items.map((item, idx) => (
        <div key={idx} className={styles.item} onClick={(e) => handleItemClick(e, item)}>
          {item.icon && <img className={styles.itemIcon} src={item.icon} alt={item.label} />}
          <div>{item.label}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`${styles.manageCell} ${openPopup ? styles.active : ""}`} style={{ ...style }}>
      <button
        className={`${styles.icon} ${styles.cursorPointer}`}
        onClick={handleClick}
        ref={buttonRef}
      >
        <img src={openPopup ? icons.menu.horizontal.blue : icons.menu.horizontal.black} />
      </button>
      {openPopup && createPortal(popupContent, document.body)}
    </div>
  );
};

const RenderRow = ({
  data,
  columns,
  checkedList,
  onCheckItem,
  onUpdateAddressOpen,
  onUpdateStateType,
  onUpdateMemo,
  onDeleteSale,
  onDetailSaleView,
  onOvBadgeClick,
}) => {
  const { startAiPost, loadingNode: aiPostLoadingNode, loading: aiPostLoading } =
    useAiPostStarter();
  const handleCheckItem = useCallback(
    (e) => {
      e.stopPropagation();
      onCheckItem(data.suid);
    },
    [data.suid, onCheckItem]
  );

  return (
    <tr
      key={data.suid}
      onClick={() => {
        onDetailSaleView(data);
      }}
      className={`
                  ${checkedList.includes(data.suid) ? styles.checkedItem : ""}
                  ${data.state_type === 1 ? styles.doneItem : ""}
            `}
    >
      {columns.map((column) => {
        switch (column.key) {
          case "checkbox":
            return (
              <td
                key={`${data.suid}_${column.key}`}
                className={styles.fixedCell}
                onClick={handleCheckItem}
              >
                <CheckboxCell value={data.suid} checked={checkedList.includes(data.suid)} />
              </td>
            );
          case "ov_status": {
            const hasActive = data.ov_active_status === 'COMPLETED' && data.ov_active_expires_at;
            const activeIsSameAsLatest = hasActive
              && data.ov_active_status === data.ov_status
              && data.ov_active_expires_at === data.ov_expires_at;
            const showTwo = hasActive && !activeIsSameAsLatest;
            return (
              <td key={`${data.suid}_${column.key}`}>
                <div className={showTwo ? styles.ovBadgeWrap : undefined}>
                  {showTwo && (
                    <OwnerVerificationBadge
                      ovStatus={data.ov_active_status}
                      ovExpiresAt={data.ov_active_expires_at}
                      onClick={(key) => onOvBadgeClick?.(data, key, data.ov_active_uuid)}
                    />
                  )}
                  <OwnerVerificationBadge
                    ovStatus={data.ov_status}
                    ovExpiresAt={data.ov_expires_at}
                    onClick={(key) => onOvBadgeClick?.(data, key)}
                  />
                </div>
              </td>
            );
          }
          case "type":
            return (
              <td key={`${data.suid}_${column.key}`}>
                <TypeCell data={data} />
              </td>
            );
          case "photo":
            return (
              <td key={`${data.suid}_${column.key}`}>
                <PhotoCell photoUrl={data.photo} />
              </td>
            );
          case "price":
            return (
              <td key={`${data.suid}_${column.key}`}>
                <PriceCell
                  dealType={data.deal_type}
                  price={data.price}
                  deposit={data.deposit}
                  monthly={data.monthly}
                />
              </td>
            );
          case "address":
            return (
              <td key={`${data.suid}_${column.key}`}>
                <AddressCell address={data.address} buildingName={data.building_name} data={data} />
              </td>
            );
          case "date":
            return (
              <td key={`${data.suid}_${column.key}`}>
                <DateCell creationDate={data.creation_date} />
              </td>
            );
          case "control":
            return (
              <td key={`${data.suid}_${column.key}`}>
                <div className={styles.controlContainer}>
                  <ToggleButton
                    title={{ checked: "매물 노출", default: "매물 비노출" }}
                    checked={data.state_type === 0}
                    disabled={data.state_type === 1}
                    onToggle={(checked) => onUpdateStateType([data.suid], checked ? 0 : 3)}
                  />
                  <ToggleButton
                    title={{ checked: "위치 공개", default: "위치 비공개" }}
                    checked={data.address_open === 1}
                    onToggle={(checked) => onUpdateAddressOpen([data.suid], checked ? 1 : 0)}
                  />
                  <ToggleButton
                    title={{ checked: "거래중", default: "거래완료" }}
                    checked={data.state_type !== 1}
                    disabled={data.state_type === 1}
                    onToggle={(checked) => onUpdateStateType([data.suid], 1)}
                  />
                </div>
              </td>
            );
          case "addition":
            return (
              <td key={`${data.suid}_${column.key}`}>
                <div className={styles.additionContainer}>
                  <AdditionButtonsMemo
                    title={"메모"}
                    icon={
                      !(
                        data["sell_name"] ||
                        data["sell_phone"] ||
                        data["buy_name"] ||
                        data["buy_phone"] ||
                        data["memo"]
                      )
                        ? icons.note.grey
                        : icons.note.blue
                    }
                    deactive={
                      !(
                        data["sell_name"] ||
                        data["sell_phone"] ||
                        data["buy_name"] ||
                        data["buy_phone"] ||
                        data["memo"]
                      )
                    }
                    data={data}
                    onClick={onUpdateMemo}
                  />
                  {legacy.loggedInUser?.getInfo()?.membership && (
                  <AdditionButtonsReport
                    title={"보고서"}
                    icon={!data.report_uuid ? icons.report.grey : icons.report.blue}
                    deactive={!data.report_uuid}
                    data={data}
                    onClick={() => {
                      legacy.openRequestReport({ address: data.address, uuid: data.suid });
                    }}
                  />
                  )}
                  <AdditionButtonsAiPost
                    data={data}
                    onStart={startAiPost}
                    disabled={aiPostLoading}
                  />
                  {aiPostLoadingNode}
                </div>
              </td>
            );
          case "manage":
            const rootRef = useRef(null);
            const share_btn = (baseEl, category, uuid) => {
              const sendUrl = window.location.origin + "/m/" + uuid;
              legacy.share_toast_target_id = legacy.jQuery(baseEl);
              legacy.set_share(sendUrl, category, "estate_agent", { position: { right: "20px" } });
            };

            return (
              <td key={`${data.suid}_${column.key}`} className={styles.fixedCell} ref={rootRef}>
                <ManageCell
                  items={[
                    {
                      icon: icons.edit.grey,
                      label: "수정",
                      onClick: () => {
                        legacy.em_modify_estate_data("sale", data.suid);
                      },
                    },
                    {
                      icon: icons.delete.grey,
                      label: "삭제",
                      onClick: () => {
                        onDeleteSale([data.suid]);
                      },
                    },
                    {
                      icon: icons.share.grey,
                      label: "공유",
                      onClick: (e) => {
                        e.stopPropagation();
                        share_btn(rootRef.current, "sale", data.suid);
                      },
                    },
                  ]}
                />
              </td>
            );
          default:
            return <td key={`${data.suid}_${column.key}`}>{data[column.key]}</td>;
        }
      })}
    </tr>
  );
};

const RenderItem = ({
  data,
  onCheckItem,
  checkedList,
  onUpdateAddressOpen,
  onUpdateStateType,
  onUpdateMemo,
  onDeleteSale,
  onDetailSaleView,
  onOvBadgeClick,
  isMobile,
}) => {
  const [showMemo, setShowMemo] = useState(false);
  const memoRef = useRef(null);
  const { startAiPost, loadingNode: aiPostLoadingNode } = useAiPostStarter();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (memoRef.current && !memoRef.current.contains(event.target)) {
        setShowMemo(false);
      }
    };

    if (showMemo) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showMemo]);

  const handleCheckItem = useCallback(
    (e) => {
      e.stopPropagation();
      onCheckItem(data.suid);
    },
    [data.suid, onCheckItem]
  );

  return (
    <div
      className={`${styles.listItem} ${data.state_type === 1 ? styles.doneItem : ""} ${checkedList.includes(data.suid) ? styles.checkedItem : ""}`}
    >
      <div className={`${styles.cursorPointer}`} onClick={() => onDetailSaleView(data)}>
        <RowFlex gap="12px">
          <div
            key={`${data.suid}_'checked`}
            style={{ position: "relative" }}
            onClick={handleCheckItem}
          >
            <CheckboxCell value={data.suid} checked={checkedList.includes(data.suid)} />
            <div>
              <PhotoCell photoUrl={data.photo} />
            </div>
          </div>

          <VerticalFlex className={`${styles.contentContainer}`} gap="4px">
            <HorizontalFlex
              leftParentClassName={styles.badgeLeftContent}
              leftChildren={
                <div className={styles.badgeScrollContainer}>
                  <div
                    className={styles.badgeScrollRow}
                  >
                    <TypeCell data={data} />
                    {isOwnerVerified(data) && <OwnerVerifiedGradientBadge />}
                  </div>
                  {data.state_type !== 1 && <div className={styles.badgeScrollFade} />}
                </div>
              }
              rightChildren={<DateDiifCell creationDate={data.creation_date} />}
            />

            <div className={styles.buildingName}>
              {data.address} {data.buildingName && `(${data.buildingName})`}
            </div>
            <PriceCell
              dealType={data.deal_type}
              price={data.price}
              deposit={data.deposit}
              monthly={data.monthly}
            />
          </VerticalFlex>
        </RowFlex>

        <RowFlex style={{ marginTop: "12px" }}>
          <div className={styles.controlContainer}>
            <ToggleButton
              title={{ checked: "매물 노출", default: "매물 비노출" }}
              checked={data.state_type === 0}
              disabled={data.state_type === 1}
              onToggle={(checked) => onUpdateStateType([data.suid], checked ? 0 : 3)}
            />
            <ToggleButton
              title={{ checked: "위치 공개", default: "위치 비공개" }}
              checked={data.address_open === 1}
              onToggle={(checked) => onUpdateAddressOpen([data.suid], checked ? 1 : 0)}
            />
            <ManageCell
              style={{ marginLeft: "auto" }}
              items={[
                {
                  icon: icons.edit.coolGrey,
                  label: "수정",
                  onClick: () => {
                    legacy.em_modify_estate_data("sale", data.suid);
                  },
                },
                {
                  icon: icons.delete.coolGrey,
                  label: "삭제",
                  onClick: () => {
                    onDeleteSale([data.suid]);
                  },
                },
                {
                  icon: icons.share.coolGrey,
                  label: "공유",
                  onClick: (e) => {
                    legacy.em_click_share_btn(e, e.target, "sale", data.suid);
                  },
                },
                {
                  icon: icons.note.coolGrey,
                  label: "메모",
                  onClick: (e) => {
                    setShowMemo((prev) => !prev);
                  },
                },
                ...(legacy.loggedInUser?.getInfo()?.membership ? [{
                  icon: icons.report.coolGrey,
                  label: "보고서",
                  onClick: (e) => {
                    legacy.openRequestReport({ address: data.address, uuid: data.suid });
                  },
                }] : []),
                {
                  icon: icons.aiPost.coolGrey,
                  label: "AI 포스트",
                  onClick: (e) => {
                    startAiPost(data);
                  },
                },
                ...(data.state_type !== 1
                  ? [
                      {
                        icon: icons.saleState.done.coolGrey,
                        label: "거래완료 상태변경",
                        onClick: (e) => {
                          onUpdateStateType([data.suid], 1);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </RowFlex>
      </div>
      {showMemo && (
        <>
          {isMobile && (
            <div className={styles.backgroundOverlay} onClick={() => setShowMemo(false)} />
          )}
          <div
            className={`${styles.popupContainer} ${isMobile ? styles.isMobile : ""}`}
            ref={memoRef}
          >
            <HorizontalFlex
              className={styles.header}
              leftChildren={<div style={{ marginTop: "8px" }}>메모 작성</div>}
              rightChildren={
                <div
                  className={styles.cursorPointer}
                  style={{ padding: "12px" }}
                  onClick={() => setShowMemo(false)}
                >
                  <img src={icons.close.black} style={{ width: "24px", height: "24px" }} />
                </div>
              }
            />
            <MemoEditor data={data} onClick={onUpdateMemo} />
          </div>
        </>
      )}
      {aiPostLoadingNode}
    </div>
  );
};
